class OpenResultError extends Error {
  code;
  constructor(code, message) {
    super(message);
    this.name = "OpenResultError";
    this.code = code;
  }
}
class UnsupportedVersionError extends OpenResultError {
  found;
  supported;
  constructor(found, supported) {
    super(
      "OR-402",
      `This document declares OpenResult ${found}, but this implementation supports ${supported}. A different major version may have incompatible semantics, so it is not interpreted rather than guessed at.`
    );
    this.name = "UnsupportedVersionError";
    this.found = found;
    this.supported = supported;
  }
}
class NotOpenResultError extends OpenResultError {
  constructor(detail) {
    super("OR-401", `Not an OpenResult document: ${detail}`);
    this.name = "NotOpenResultError";
  }
}
const SUPPORTED_VERSION = { major: 1, minor: 0 };
const VERSION_PATTERN = /^(\d+)\.(\d+)$/;
function isOpenResult(value) {
  if (value === null || typeof value !== "object") return false;
  const candidate = value;
  return typeof candidate["openresult"] === "string" && VERSION_PATTERN.test(candidate["openresult"]);
}
function formatVersion(document) {
  const declared = document.openresult;
  const match = typeof declared === "string" ? VERSION_PATTERN.exec(declared) : null;
  if (!match) {
    return failVersion(declared);
  }
  return { major: Number(match[1]), minor: Number(match[2]) };
}
function failVersion(declared) {
  throw new NotOpenResultError(
    declared === void 0 ? 'the "openresult" member is missing. Every document must declare the format version it conforms to, for example "openresult": "1.0".' : `"openresult": ${JSON.stringify(declared)} is not a MAJOR.MINOR version string.`
  );
}
function parse(input) {
  let value = input;
  if (typeof input === "string") {
    try {
      value = JSON.parse(input);
    } catch (error) {
      throw new NotOpenResultError(`the input is not valid JSON (${error.message}).`);
    }
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new NotOpenResultError("the document must be a JSON object.");
  }
  const document = value;
  const version = formatVersion(document);
  if (version.major !== SUPPORTED_VERSION.major) {
    throw new UnsupportedVersionError(
      document.openresult,
      `${SUPPORTED_VERSION.major}.${SUPPORTED_VERSION.minor}`
    );
  }
  if (!Array.isArray(document.participants)) {
    throw new NotOpenResultError('the "participants" member is missing or is not an array.');
  }
  if (!Array.isArray(document.results)) {
    throw new NotOpenResultError('the "results" member is missing or is not an array.');
  }
  return document;
}
const DEFAULT_EXCLUDED_STATUSES = [
  "notClassified",
  "inProgress",
  "dnf",
  "dns",
  "dsq",
  "outOfTime",
  "withdrawn"
];
const KNOWN_STATUSES = /* @__PURE__ */ new Set([
  "finished",
  "bye",
  ...DEFAULT_EXCLUDED_STATUSES
]);
const KNOWN_BETTER_WHEN = /* @__PURE__ */ new Set(["lower", "higher", "none"]);
const KNOWN_TIES = /* @__PURE__ */ new Set(["standard", "dense", "strict", "resolved"]);
function normalizeStatus(status) {
  if (status === void 0) return "finished";
  return KNOWN_STATUSES.has(status) ? status : "finished";
}
function normalizeBetterWhen(betterWhen) {
  if (betterWhen === void 0) return "none";
  return KNOWN_BETTER_WHEN.has(betterWhen) ? betterWhen : "none";
}
function normalizeTies(ties) {
  if (ties === void 0) return "standard";
  return KNOWN_TIES.has(ties) ? ties : "standard";
}
function measure(document, id) {
  return document.measures?.find((candidate) => candidate.id === id);
}
function attribute(document, id) {
  return document.attributeDefinitions?.find((candidate) => candidate.id === id);
}
function participant(document, id) {
  return document.participants.find((candidate) => candidate.id === id);
}
function event(document, id) {
  return document.events?.find((candidate) => candidate.id === id);
}
function category(document, id) {
  return document.categories?.find((candidate) => candidate.id === id);
}
function participantsOf(document, categoryId) {
  if (categoryId === void 0) return document.participants;
  const members = new Set(category(document, categoryId)?.participants ?? []);
  return document.participants.filter((candidate) => members.has(candidate.id));
}
function resultsOf(document, filter = {}) {
  return document.results.filter(
    (result) => (filter.event === void 0 || result.event === filter.event) && (filter.participant === void 0 || result.participant === filter.participant)
  );
}
function eventWithDescendants(document, eventId) {
  const included = /* @__PURE__ */ new Set([eventId]);
  const events = document.events ?? [];
  let grew = true;
  while (grew) {
    grew = false;
    for (const candidate of events) {
      if (candidate.parent !== void 0 && included.has(candidate.parent) && !included.has(candidate.id)) {
        included.add(candidate.id);
        grew = true;
      }
    }
  }
  return included;
}
function isRankable(result, excludedStatuses) {
  const excluded = excludedStatuses ?? DEFAULT_EXCLUDED_STATUSES;
  return !excluded.includes(normalizeStatus(result.status));
}
function usedMeasures(document) {
  const used = /* @__PURE__ */ new Set();
  for (const result of document.results) {
    for (const key of Object.keys(result.values ?? {})) used.add(key);
  }
  return (document.measures ?? []).filter((candidate) => used.has(candidate.id));
}
function supersedes(a, b) {
  if (a.id === void 0 || b.id === void 0 || a.id !== b.id) return void 0;
  const left = a.version ?? -1;
  const right = b.version ?? -1;
  if (left !== right) return left > right;
  if (a.status === void 0 || b.status === void 0) return void 0;
  const authority = standingOf(a.status) - standingOf(b.status);
  return authority === 0 ? void 0 : authority > 0;
}
function standingOf(status) {
  if (status === "official" || status === "amended") return 2;
  if (status === "provisional") return 1;
  return 0;
}
function listRankings(document) {
  const declared = document.rankings ?? [];
  if (declared.length > 0) {
    return declared.map((ranking) => ({
      id: ranking.id,
      label: ranking.label,
      implicit: false,
      sortBy: ranking.sortBy,
      ties: normalizeTies(ranking.ties)
    }));
  }
  const implicit = implicitRanking(document);
  return implicit === void 0 ? [] : [
    {
      id: implicit.id,
      label: implicit.label,
      implicit: true,
      sortBy: implicit.sortBy,
      ties: normalizeTies(implicit.ties)
    }
  ];
}
function implicitRanking(document) {
  const rankable = (document.measures ?? []).find((candidate) => sortable(document, candidate.id));
  if (rankable === void 0) return void 0;
  return {
    id: rankable.id,
    label: rankable.label,
    sortBy: [rankable.id],
    ties: "standard"
  };
}
function resolveRanking(document, rankingId) {
  const declared = document.rankings ?? [];
  if (rankingId !== void 0) {
    const found = declared.find((candidate) => candidate.id === rankingId);
    if (found !== void 0) return found;
    if (declared.length > 0) return void 0;
    const implicit = implicitRanking(document);
    return implicit?.id === rankingId ? implicit : void 0;
  }
  return declared[0] ?? implicitRanking(document);
}
function selectResults(document, ranking) {
  const scope = ranking.scope;
  if (scope === void 0) return document.results;
  const wantedCategories = scope.category === void 0 ? void 0 : new Set(Array.isArray(scope.category) ? scope.category : [scope.category]);
  const members = wantedCategories === void 0 ? void 0 : new Set(
    (document.categories ?? []).filter((candidate) => wantedCategories.has(candidate.id)).flatMap((candidate) => candidate.participants ?? [])
  );
  const events = scope.event === void 0 ? void 0 : new Set(Array.isArray(scope.event) ? scope.event : [scope.event]);
  return document.results.filter((result) => {
    if (events !== void 0 && (result.event === void 0 || !events.has(result.event)))
      return false;
    if (members !== void 0 && !members.has(result.participant)) return false;
    return true;
  });
}
const KNOWN_KINDS = /* @__PURE__ */ new Set([
  "duration",
  "distance",
  "mass",
  "points",
  "score",
  "percentage",
  "count",
  "money",
  "rate",
  "text",
  "boolean"
]);
function sortable(document, measureId) {
  const definition = measure(document, measureId);
  if (definition === void 0) return false;
  if (normalizeBetterWhen(definition.betterWhen) === "none") return false;
  return definition.kind !== "text" && definition.kind !== "boolean";
}
function expectedType(kind) {
  if (kind === "text") return "string";
  if (kind === "boolean") return "boolean";
  return "number";
}
function carriesUsableValue(document, result, measureId) {
  const value = result.values?.[measureId];
  if (value === void 0 || value === null) return false;
  return typeof value === expectedType(measure(document, measureId)?.kind);
}
function compareValues(a, b, betterWhen) {
  if (typeof a !== "number" || typeof b !== "number") return 0;
  const ascending = a - b;
  if (ascending === 0) return 0;
  return betterWhen === "higher" ? -ascending : ascending;
}
function compareResults(document, sortBy, a, b) {
  for (const measureId of sortBy) {
    const betterWhen = normalizeBetterWhen(measure(document, measureId)?.betterWhen);
    const left = a.values?.[measureId];
    const right = b.values?.[measureId];
    if (left === void 0 || right === void 0) continue;
    const comparison = compareValues(left, right, betterWhen);
    if (comparison !== 0) return comparison;
  }
  return 0;
}
function settleByPublishedRanks(group, rankingId) {
  const positions = group.map((result) => {
    const published = result.ranks?.[rankingId];
    return Number.isInteger(published) && published > 0 ? published : void 0;
  });
  if (positions.some((position) => position === void 0)) return void 0;
  if (new Set(positions).size !== positions.length) return void 0;
  return [...group].sort((a, b) => (a.ranks?.[rankingId] ?? 0) - (b.ranks?.[rankingId] ?? 0));
}
function rank(document, rankingId) {
  const ranking = resolveRanking(document, rankingId);
  const participants = new Map(document.participants.map((entry) => [entry.id, entry]));
  const toEntry = (result, entryRank, tiedWith) => ({
    rank: entryRank,
    participant: participants.get(result.participant) ?? unknownParticipant(result.participant),
    result,
    values: result.values ?? {},
    tiedWith
  });
  if (ranking === void 0) {
    return document.results.map((result) => toEntry(result, null, []));
  }
  const excluded = ranking.excludeStatuses ?? DEFAULT_EXCLUDED_STATUSES;
  const selected = selectResults(document, ranking);
  const sortBy = ranking.sortBy.filter((measureId) => sortable(document, measureId));
  const rankable = [];
  const unranked = [];
  for (const result of selected) {
    const statusAllows = !excluded.includes(normalizeStatus(result.status));
    const hasEveryMeasure = sortBy.every(
      (measureId) => carriesUsableValue(document, result, measureId)
    );
    (statusAllows && hasEveryMeasure ? rankable : unranked).push(result);
  }
  const ties = normalizeTies(ranking.ties);
  const sorted = [...rankable].sort((a, b) => compareResults(document, sortBy, a, b));
  const entries = [];
  let groupStart = 0;
  let groupNumber = 0;
  while (groupStart < sorted.length) {
    groupNumber += 1;
    const first = sorted[groupStart];
    if (first === void 0) break;
    let groupEnd = groupStart + 1;
    while (groupEnd < sorted.length) {
      const next = sorted[groupEnd];
      if (next === void 0 || compareResults(document, sortBy, first, next) !== 0) break;
      groupEnd += 1;
    }
    const group = sorted.slice(groupStart, groupEnd);
    const settled = ties === "resolved" && group.length > 1 ? settleByPublishedRanks(group, ranking.id) : void 0;
    if (settled !== void 0) {
      settled.forEach((result, offset) => {
        entries.push(toEntry(result, groupStart + offset + 1, []));
      });
      groupStart = groupEnd;
      continue;
    }
    const assigned = ties === "dense" ? groupNumber : groupStart + 1;
    const tiedIds = group.length > 1 ? group.map((result) => result.participant) : [];
    for (const result of group) {
      entries.push(
        toEntry(
          result,
          assigned,
          tiedIds.filter((id) => id !== result.participant)
        )
      );
    }
    groupStart = groupEnd;
  }
  for (const result of unranked) {
    entries.push(toEntry(result, null, []));
  }
  return entries;
}
function unknownParticipant(id) {
  return { id, name: id };
}
function formatNumber(value, measure2) {
  if (measure2.kind === "duration" && isTimeUnit(measure2.unit)) {
    return formatDuration(toSeconds(value, measure2.unit), measure2.precision ?? 0);
  }
  return measure2.precision === void 0 ? String(value) : toDecimalPlaces(value, measure2.precision);
}
function formatValue(value, measure2, options = {}) {
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "string") return value;
  const { locale, humanizeDuration = true, showScale = true } = options;
  const precision = measure2.precision;
  if (measure2.kind === "duration" && humanizeDuration && isTimeUnit(measure2.unit)) {
    return formatDuration(toSeconds(value, measure2.unit), precision ?? 0);
  }
  const formatted = decimal(value, precision, locale);
  if (showScale && measure2.max !== void 0 && measure2.max !== 1 && normalizeBetterWhen(measure2.betterWhen) === "higher" && (measure2.kind === "score" || measure2.kind === "points")) {
    return `${formatted}/${decimal(measure2.max, precision, locale)}`;
  }
  return measure2.unit === void 0 ? formatted : `${formatted} ${measure2.unit}`;
}
function formatAttribute(value, attribute2, options = {}) {
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value !== "number") return value;
  const formatted = decimal(value, void 0, options.locale);
  return attribute2.unit === void 0 ? formatted : `${formatted} ${attribute2.unit}`;
}
const MOST_DECIMALS = 100;
function fractionDigits(precision) {
  return Math.min(Math.max(Math.trunc(precision), 0), MOST_DECIMALS);
}
function decimal(value, precision, locale) {
  return precision === void 0 ? new Intl.NumberFormat(locale).format(value) : new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits(precision),
    maximumFractionDigits: fractionDigits(precision)
  }).format(value);
}
function isTimeUnit(unit) {
  return unit === "s" || unit === "ms" || unit === "min" || unit === "h";
}
function toSeconds(value, unit) {
  switch (unit) {
    case "ms":
      return value / 1e3;
    case "min":
      return value * 60;
    case "h":
      return value * 3600;
    default:
      return value;
  }
}
function toDecimalPlaces(value, precision) {
  return new Intl.NumberFormat("en", {
    minimumFractionDigits: fractionDigits(precision),
    maximumFractionDigits: fractionDigits(precision),
    useGrouping: false
  }).format(value);
}
function formatDuration(totalSeconds, precision) {
  const negative = totalSeconds < 0;
  const absolute = Math.abs(totalSeconds);
  const hours = Math.floor(absolute / 3600);
  const minutes = Math.floor(absolute % 3600 / 60);
  const seconds = absolute % 60;
  const secondsText = toDecimalPlaces(seconds, precision);
  const padded = seconds < 10 ? `0${secondsText}` : secondsText;
  let text;
  if (hours > 0) {
    text = `${hours}:${String(minutes).padStart(2, "0")}:${padded}`;
  } else if (minutes > 0) {
    text = `${minutes}:${padded}`;
  } else {
    text = secondsText;
  }
  return negative ? `-${text}` : text;
}
function serialize(document, options = {}) {
  const indent = options.indent ?? 0;
  return JSON.stringify(document, null, indent > 0 ? indent : void 0);
}
export {
  DEFAULT_EXCLUDED_STATUSES,
  KNOWN_KINDS,
  NotOpenResultError,
  OpenResultError,
  SUPPORTED_VERSION,
  UnsupportedVersionError,
  attribute,
  category,
  event,
  eventWithDescendants,
  formatAttribute,
  formatNumber,
  formatValue,
  formatVersion,
  isOpenResult,
  isRankable,
  listRankings,
  measure,
  normalizeBetterWhen,
  normalizeStatus,
  normalizeTies,
  parse,
  participant,
  participantsOf,
  rank,
  resultsOf,
  serialize,
  supersedes,
  usedMeasures
};
//# sourceMappingURL=index.js.map
